import { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { servicesApi } from '../../api/servicesApi';
import { Button } from '../../components/ui/Button';
import { MediaPicker } from '../../components/admin/MediaPicker';

const API_URL = process.env.REACT_APP_API_URL;

const UNIVERS_LABELS = {
  particulier: 'Particulier',
  professionnel: 'Professionnel',
  commun: 'Commun (les deux)',
};

const VIDE = {
  univers: 'particulier',
  category_id: '',
  image_id: '',
  image_url: '',
  titre: '',
  description_courte: '',
  description: '',
  statut: 'brouillon',
};

export function ServicesManager() {
  const { data: services, loading, refetch } = useFetch(() => servicesApi.list(), []);
  const { data: categories } = useFetch(() => servicesApi.categories(), []);
  const [edition, setEdition] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  function ouvrirCreation() {
    setEdition({ ...VIDE });
    setErrors({});
  }

  function ouvrirEdition(service) {
    setEdition({ ...VIDE, ...service });
    setErrors({});
  }

  function handleUniversChange(univers) {
    // La catégorie choisie peut ne plus correspondre au nouvel univers : on la réinitialise
    // sauf si elle reste valide (catégorie de cet univers, ou univers "commun").
    const categorieValide = categories?.find((cat) => cat.id === Number(edition.category_id));
    const resteValide = univers === 'commun' || categorieValide?.univers === univers;

    setEdition({ ...edition, univers, category_id: resteValide ? edition.category_id : '' });
  }

  const categoriesDisponibles =
    edition?.univers === 'commun'
      ? categories
      : categories?.filter((cat) => cat.univers === edition?.univers);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      const payload = {
        ...edition,
        image_id: edition.image_id || null,
      };
      if (edition.id) {
        await servicesApi.update(edition.id, payload);
      } else {
        await servicesApi.create(payload);
      }
      setEdition(null);
      refetch();
    } catch (err) {
      setErrors(err.errors || { _global: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer ce service ?')) return;
    await servicesApi.remove(id);
    refetch();
  }

  if (loading) return <p>Chargement...</p>;

  return (
    <section>
      <div className="admin-toolbar">
        <h1>Services</h1>
        {!edition && <Button onClick={ouvrirCreation}>+ Nouveau service</Button>}
      </div>

      {edition && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-form__field">
            <label>Univers</label>
            <select value={edition.univers} onChange={(e) => handleUniversChange(e.target.value)}>
              <option value="particulier">Particulier</option>
              <option value="professionnel">Professionnel</option>
              <option value="commun">Commun (visible dans les deux)</option>
            </select>
            {errors.univers && <span className="admin-form__error">{errors.univers}</span>}
          </div>

          <div className="admin-form__field">
            <label>Catégorie</label>
            <select
              value={edition.category_id}
              onChange={(e) => setEdition({ ...edition, category_id: e.target.value })}
              required
            >
              <option value="">Sélectionner...</option>
              {categoriesDisponibles?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom} ({cat.univers})
                </option>
              ))}
            </select>
            {errors.category_id && <span className="admin-form__error">{errors.category_id}</span>}
            {!categoriesDisponibles?.length && (
              <span className="admin-form__error">
                Aucune catégorie pour cet univers — créez-en une dans Catégories.
              </span>
            )}
          </div>

          <MediaPicker
            label="Image"
            value={edition.image_url}
            onChange={(media) =>
              setEdition({ ...edition, image_id: media?.id || '', image_url: media?.chemin || '' })
            }
          />

          <div className="admin-form__field">
            <label>Titre</label>
            <input value={edition.titre} onChange={(e) => setEdition({ ...edition, titre: e.target.value })} required />
            {errors.titre && <span className="admin-form__error">{errors.titre}</span>}
          </div>

          <div className="admin-form__field">
            <label>Description courte</label>
            <input
              value={edition.description_courte || ''}
              onChange={(e) => setEdition({ ...edition, description_courte: e.target.value })}
            />
          </div>

          <div className="admin-form__field">
            <label>Description</label>
            <textarea
              rows={6}
              value={edition.description}
              onChange={(e) => setEdition({ ...edition, description: e.target.value })}
              required
            />
            {errors.description && <span className="admin-form__error">{errors.description}</span>}
          </div>

          <div className="admin-form__field">
            <label>Statut</label>
            <select value={edition.statut} onChange={(e) => setEdition({ ...edition, statut: e.target.value })}>
              <option value="brouillon">Brouillon</option>
              <option value="publie">Publié</option>
              <option value="archive">Archivé</option>
            </select>
          </div>

          {errors._global && <p className="admin-form__error">{errors._global}</p>}

          <div className="admin-form__actions">
            <Button type="submit" disabled={submitting}>
              {edition.id ? 'Enregistrer' : 'Créer'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEdition(null)}>
              Annuler
            </Button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th></th>
            <th>Titre</th>
            <th>Univers</th>
            <th>Statut</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {services?.map((s) => (
            <tr key={s.id}>
              <td>
                {s.image_url ? (
                  <img src={`${API_URL}${s.image_url}`} alt="" className="admin-table__thumb" />
                ) : (
                  <div className="admin-table__thumb--empty" />
                )}
              </td>
              <td>{s.titre}</td>
              <td>{UNIVERS_LABELS[s.univers] || s.univers}</td>
              <td>{s.statut}</td>
              <td className="admin-table__actions">
                <button onClick={() => ouvrirEdition(s)}>Modifier</button>
                <button onClick={() => handleDelete(s.id)}>Supprimer</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
